'use client';

import { createContext, useContext, type RefObject } from 'react';

import type { HeaderProps } from '@/components/layout/header/Header.types';

/** The two global layout bars a site-page block can replace. */
export type SiteChromeKind = 'header' | 'footer';

/** What the HEADER block needs to reuse the layout's logo and account menu. */
export interface SiteChromeAccount {
  isLoggedIn: boolean;
  /** Set only when signed in. */
  userName?: string;
  onAvatarClick?: () => void;
  isMenuOpen: boolean;
  /** `/login` with a return URL for the current page (`useLoginHref`). */
  loginHref: string;
  logoSrc: string;
  logoAlt: string;
}

export interface SiteChromeValue {
  /**
   * Declares that a HEADER / FOOTER block is mounted. Returns the function that withdraws the
   * declaration; call it from an effect cleanup.
   */
  registerChrome: (kind: SiteChromeKind) => () => void;
  account: SiteChromeAccount | null;
  /**
   * Hands the layout the ref of the block's OWN avatar button, so the layout's `UserMenu` anchors
   * there while the global Header is suppressed. Returns the function that withdraws it; call it
   * from an effect cleanup.
   *
   * The block never attaches the layout's `useUserMenu().anchorRef`: that ref is the global
   * Header's, and when the Header unmounts React nulls it, so a shared ref would leave the menu
   * with nothing to position against.
   */
  registerAvatarAnchor: (ref: RefObject<HTMLButtonElement | null>) => () => void;
  /**
   * The layout's active-session pill, exactly the props the global Header receives (null for a
   * guest or with no session). Passed through rather than recomputed so `/` runs ONE
   * `useActiveSessionTimer` whichever header is showing.
   */
  activeSessionIndicator?: HeaderProps['activeSessionIndicator'];
  /** The popup the pill opens, exactly as the global Header receives it. */
  activeSessionsPopup?: HeaderProps['activeSessionsPopup'];
  /**
   * True while the published site navigation (PB-9032) draws the header and footer on every page.
   * A HEADER / FOOTER block then renders nothing and does not register. Absent means false.
   */
  siteNavPublished?: boolean;
  /**
   * The published navigation's raw footer slots (null when it has none). While `siteNavPublished`,
   * the homepage FOOTER block renders THESE in place of its own slots; with none it yields.
   */
  siteNavFooterSlots?: Record<string, unknown> | null;
}

/**
 * Outside a provider the context is inert: registering does nothing, so a block rendered in a
 * test or a future surface that has no layout chrome simply keeps whatever bars exist.
 */
const INERT: SiteChromeValue = {
  registerChrome: () => () => {},
  account: null,
  registerAvatarAnchor: () => () => {},
};

/**
 * Lets a HEADER / FOOTER block tell the public layout to hide its global Header / Footer.
 *
 * ## Invariants:
 * - The layout, not the block, decides what is hidden: it suppresses a global bar only while a
 *   block of that kind is registered AND the pathname is `/`. Every other route keeps both bars.
 * - Registration is cleaned up on unmount, so leaving `/` or a block disappearing (audience,
 *   re-publish) always restores the global bar.
 * - While `siteNavPublished` is true the HEADER block yields (render null, never register). The
 *   FOOTER block renders the navigation's footer slots in place (and keeps its registration), or
 *   yields too when the navigation has none.
 * - A block and the global Header never attach the same ref object. Each owns the element it
 *   anchors overlays to; the layout chooses which ref an overlay reads (the block's while the
 *   Header is suppressed, the Header's otherwise).
 */
export const SiteChromeContext = createContext<SiteChromeValue>(INERT);

export function useSiteChrome(): SiteChromeValue {
  return useContext(SiteChromeContext);
}
